/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { Editor } from '@monaco-editor/react';
import { Code, AlertCircle, Bot } from 'lucide-react';
import * as monaco from 'monaco-editor';
import babelPlugin from 'prettier/plugins/babel';
import estreePlugin from 'prettier/plugins/estree';
import tsPlugin from 'prettier/plugins/typescript';
import * as prettier from 'prettier/standalone';
import { useMemo, useRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from '../ui/use-toast';

import { generateBlockCode } from './helpers/blockCodeGenerator';
import { generateBlockTypeDefinitions } from './helpers/codeEditorHelpers';
import { AstParser } from './managers/AstParser';
import { StepConverter } from './managers/StepConverter';
import { TabManager, TabManagerRef, StoryInfo, TabInfo } from './TabManager';
// Bundle the healLib.d.ts file using vite raw import
import fakerDts from './types/faker.d.ts?raw';
import formanceDts from './types/formance.d.ts?raw';
import healLibDts from './types/healLib.d.ts?raw';
import otpauthDts from './types/otpauth.d.ts?raw';
import playwrightDts from './types/playwright.d.ts?raw';

import { Step as UIStep, Story } from '@/pages/projects/LiveAgentTypes';

interface LiveAgentEditorProps {
    steps: UIStep[];
    height?: string;
    executeStep?: (step: UIStep) => void;
    dispatch: (action: any) => void;
    queue?: number[];
    isDisabled?: boolean;
    hidePlaywright?: boolean;
    variables: { [key: string]: string };
    highlightSelectors?: (
        selectors: { [key: string]: { selector: string; unique: boolean } } | null,
        force?: boolean,
    ) => void;
    showCodeAgent?: boolean;
    onToggleCodeAgent?: () => void;
    className?: string;
    readOnly?: boolean;
    blocksData?: Story[];
    isEditingBlock?: boolean;
    blockName?: string;
    blockDefArgs?: string[];
}

export interface LiveAgentEditorRef {
    getStoryInfo: () => StoryInfo;
    getTabInfo: (tabKey: string) => TabInfo;
    isSyntaxValid: () => boolean;
    getAllModifiedTabs: () => string[];
    markTabAsSaved: (tabKey: string) => void;
    dispatchBlockSteps: (blockName: string, blockSteps: UIStep[]) => boolean;
    getActiveTabId: () => string;
}

export const LiveAgentEditor = forwardRef<LiveAgentEditorRef, LiveAgentEditorProps>(
    (
        {
            steps,
            height = '100%',
            executeStep,
            dispatch,
            queue,
            isDisabled,
            hidePlaywright,
            variables,
            highlightSelectors,
            showCodeAgent,
            onToggleCodeAgent,
            className = '',
            readOnly = false,
            blocksData,
            isEditingBlock,
            blockName,
            blockDefArgs,
        },
        ref,
    ) => {
        const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();
        const tabManagerRef = useRef<TabManagerRef>(null);
        const astParser = useMemo(() => new AstParser(), []);
        const [syntaxError, setSyntaxError] = useState<{
            hasError: boolean;
            messages: string[];
        }>({ hasError: false, messages: [] });

        // === INITIAL CODE STATE ===
        // We need the initial code before mounting TabManager.  We compute it once and store in state.
        const [initCode, setInitCode] = useState<string>('');

        // Compute initial code only once at initialization
        useEffect(() => {
            let cancelled = false;

            const computeCode = async () => {
                if (isEditingBlock && blockName) {
                    // Use helper to generate a properly-formatted block wrapper
                    const blockCode = await generateBlockCode(blockName, steps, blockDefArgs || []);
                    if (!cancelled) {
                        setInitCode(blockCode);
                    }
                } else {
                    const code = StepConverter.stepsToCode(steps);
                    setInitCode(code || '// Write your code here');
                }
            };

            computeCode();

            return () => {
                cancelled = true;
            };
        }, []); // Empty dependency array to run only once at mount

        const pendingExecutionRef = useRef<{ step: UIStep } | null>(null);

        // Method to get current story info - delegate to TabManager
        const getStoryInfo = (): StoryInfo => {
            return (
                tabManagerRef.current?.getStoryInfo() || { steps, blockDefArgs: undefined, blockInputArgs: undefined }
            );
        };

        // Method to get tab info - delegate to TabManager
        const getTabInfo = (tabKey: string): TabInfo => {
            return (
                tabManagerRef.current?.getTabInfo(tabKey) || {
                    steps: [],
                    blockDefArgs: undefined,
                    blockInputArgs: undefined,
                }
            );
        };

        // Method to check if the syntax is valid - delegate to TabManager
        const isSyntaxValid = () => {
            return tabManagerRef.current?.isSyntaxValid() ?? true;
        };

        // Method to get all modified tabs - delegate to TabManager
        const getAllModifiedTabs = () => {
            return tabManagerRef.current?.getAllModifiedTabs() || [];
        };

        // Method to mark a tab as saved - delegate to TabManager
        const markTabAsSaved = (tabKey: string) => {
            tabManagerRef.current?.markTabAsSaved(tabKey);
        };

        // Method to dispatch block steps - delegate to TabManager
        const dispatchBlockSteps = (inputBlockName: string, blockSteps: UIStep[]) => {
            return tabManagerRef.current?.dispatchBlockSteps(inputBlockName, blockSteps) ?? false;
        };

        // Method to getActiveTabId current tab id - delegate to TabManager
        const getActiveTabId = () => {
            return tabManagerRef.current?.getActiveTabId() || 'main';
        };

        // Expose methods to parent component via ref
        useImperativeHandle(
            ref,
            () => ({
                getStoryInfo,
                getTabInfo,
                isSyntaxValid,
                getAllModifiedTabs,
                markTabAsSaved,
                dispatchBlockSteps,
                getActiveTabId,
            }),
            [getStoryInfo, getTabInfo, getAllModifiedTabs, markTabAsSaved, dispatchBlockSteps, getActiveTabId],
        );

        const registerPrettier = (monacoInstance: typeof monaco) => {
            const makeProvider = (
                lang: 'javascript' | 'typescript',
                parser: 'babel' | 'typescript',
                plugins: any[],
            ) => ({
                provideDocumentFormattingEdits: async (model: monaco.editor.ITextModel) => {
                    const formatted = await prettier.format(model.getValue(), {
                        parser,
                        plugins,
                        semi: true,
                        singleQuote: true,
                        tabWidth: 4,
                        trailingComma: 'none',
                        printWidth: 80,
                    });

                    return [
                        {
                            range: model.getFullModelRange(),
                            text: formatted,
                        },
                    ];
                },
            });

            monacoInstance.languages.registerDocumentFormattingEditProvider(
                'javascript',
                makeProvider('javascript', 'babel', [babelPlugin, estreePlugin]),
            );

            monacoInstance.languages.registerDocumentFormattingEditProvider(
                'typescript',
                makeProvider('typescript', 'typescript', [tsPlugin, estreePlugin]),
            );
        };

        const handleEditorBeforeMount = (monacoInstance: typeof monaco) => {
            // Generate block definitions and replace placeholder in healLib.d.ts
            const blockDefinitions =
                blocksData && blocksData.length > 0
                    ? generateBlockTypeDefinitions(blocksData)
                    : '        // No blocks available';

            const healLibWithBlocks = healLibDts.replace('/*HEAL_BLOCKS_PLACEHOLDER*/', blockDefinitions);

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                healLibWithBlocks,
                'inmemory://model/healLib.d.ts',
            );

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                playwrightDts,
                'inmemory://model/playwright.d.ts',
            );

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(fakerDts, 'inmemory://model/faker.d.ts');

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                otpauthDts,
                'inmemory://model/otpauth.d.ts',
            );

            monacoInstance.languages.typescript.javascriptDefaults.addExtraLib(
                formanceDts,
                'inmemory://model/formance.d.ts',
            );

            registerPrettier(monacoInstance);
        };

        const executeStepLocal = (step: UIStep) => {
            // Save the pending execution
            pendingExecutionRef.current = { step };

            // Update state
            dispatch({
                type: 'set',
                payload: getStoryInfo().steps,
            });
        };

        // This useEffect handles the execution after state update
        useEffect(() => {
            if (pendingExecutionRef.current && executeStep) {
                const { step } = pendingExecutionRef.current;
                executeStep(step);
                pendingExecutionRef.current = null;
            }
        }, [steps, executeStep]);

        // Update block type definitions when blocksData changes
        useEffect(() => {
            // Generate block definitions and replace placeholder in healLib.d.ts
            const blockDefinitions =
                blocksData && blocksData.length > 0
                    ? generateBlockTypeDefinitions(blocksData)
                    : '        // No blocks available';

            const healLibWithBlocks = healLibDts.replace('/*HEAL_BLOCKS_PLACEHOLDER*/', blockDefinitions);

            monaco.languages.typescript.javascriptDefaults.addExtraLib(
                healLibWithBlocks,
                'inmemory://model/healLib.d.ts',
            );
        }, [blocksData]);

        const handleFormatCode = () => {
            if (editorRef.current) {
                editorRef.current.getAction('editor.action.formatDocument')?.run();
            }
        };

        const showSelector = () => {
            if (!editorRef.current) return;

            const selection = editorRef.current.getSelection();
            if (!selection) return;

            const model = editorRef.current.getModel();
            if (!model) return;

            const selectedText = model.getValueInRange(selection);
            if (!selectedText) return;

            const code = model.getValue();
            // Analyze the selected text using our new method
            const analysis = astParser.analyzeHighlightedText(code, selectedText);

            if (analysis.type === 'variable-method' || analysis.type === 'function') {
                if (
                    analysis.functionPart &&
                    (analysis.functionPart.startsWith('getBy') || analysis.functionPart.startsWith('locator'))
                ) {
                    // Highlight the selector
                    highlightSelectors?.({ playwrightCode: { selector: analysis.functionPart || '', unique: true } });
                    return;
                }
            }

            highlightSelectors?.(null);

            toast({
                title: 'Not Supported',
                description: `The selector ${selectedText} is not supported.`,
                variant: 'destructive',
            });
        };

        const handleEditorMount = (editor: monaco.editor.IStandaloneCodeEditor) => {
            editorRef.current = editor;

            // Notify TabManager that editor is fully ready
            tabManagerRef.current?.onEditorReady();
        };

        // Handle syntax errors from TabManager
        const handleSyntaxError = (hasError: boolean, messages: string[]) => {
            setSyntaxError({ hasError, messages });
        };

        return (
            <div className={className} style={{ height, width: '100%' }}>
                <div className="relative h-full w-full flex flex-col">
                    {/* TabManager renders TabBar at the top level */}
                    {initCode && (
                        <TabManager
                            ref={tabManagerRef}
                            initialCode={initCode}
                            editorRef={editorRef}
                            astParser={astParser}
                            steps={steps}
                            isDisabled={isDisabled}
                            hidePlaywright={hidePlaywright}
                            variables={variables}
                            queue={queue}
                            executeStep={executeStepLocal}
                            showSelector={showSelector}
                            highlightSelectors={highlightSelectors}
                            onSyntaxError={handleSyntaxError}
                            readOnly={readOnly}
                            blocksData={blocksData}
                        />
                    )}

                    {/* Editor */}
                    <div className="flex-1">
                        {initCode && (
                            <Editor
                                height="100%"
                                width="100%"
                                defaultLanguage="javascript"
                                beforeMount={handleEditorBeforeMount}
                                onMount={handleEditorMount}
                                value={initCode}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 12,
                                    lineNumbers: 'on',
                                    roundedSelection: false,
                                    readOnly,
                                    automaticLayout: true,
                                    glyphMargin: !readOnly,
                                    fixedOverflowWidgets: true,
                                }}
                                theme="vs-dark"
                            />
                        )}
                    </div>

                    {/* Floating Action Buttons */}
                    <div className="absolute bottom-6 right-6 z-10 flex gap-2">
                        {syntaxError.hasError && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button className="bg-red-800 hover:bg-red-700 rounded p-1.5">
                                            <AlertCircle size={16} className="text-white" />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-md">
                                        <div>
                                            <p className="font-semibold">Syntax Error:</p>
                                            {syntaxError.messages.map((msg, i) => (
                                                <p key={i} className="text-xs">
                                                    {msg}
                                                </p>
                                            ))}
                                        </div>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        {onToggleCodeAgent && (
                            <button
                                onClick={onToggleCodeAgent}
                                className={`rounded p-1.5 ${
                                    showCodeAgent
                                        ? 'bg-purple-700 hover:bg-purple-600'
                                        : 'bg-slate-700 hover:bg-slate-600'
                                }`}
                                title={showCodeAgent ? 'Hide Code Agent' : 'Show Code Agent'}
                            >
                                <Bot size={16} className="text-white" />
                            </button>
                        )}
                        <button
                            onClick={handleFormatCode}
                            className="bg-slate-700 hover:bg-slate-600 rounded p-1.5"
                            title="Format code"
                        >
                            <Code size={16} className="text-white" />
                        </button>
                    </div>
                </div>
            </div>
        );
    },
);
